import React from 'react';
import { Calculator } from 'lucide-react';

interface CostTableEditorProps {
  settings: any;
  content: any;
  onSettingsChange: (settings: any) => void;
  onContentChange: (content: any) => void;
}

const CostTableEditor: React.FC<CostTableEditorProps> = ({ 
  settings, 
  content, 
  onSettingsChange, 
  onContentChange 
}) => {
  const handleSettingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' 
      ? (e.target as HTMLInputElement).checked 
      : type === 'number' 
        ? parseInt(value) 
        : value;
    
    onSettingsChange({ [name]: newValue });
  };
  
  const handleContentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    onContentChange({ [name]: value });
  };
  
  return (
    <div className="space-y-6">
      {/* General Settings */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Genel Ayarlar</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Başlık
            </label>
            <input
              type="text"
              name="title"
              value={settings?.title || 'Maliyet Tablosu'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Alt Başlık
            </label>
            <input
              type="text"
              name="subtitle"
              value={settings?.subtitle || 'Kredi maliyetlerini şeffaf bir şekilde görüntüleyin'}
              onChange={handleSettingChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            Kredi Türü
          </label>
          <select
            name="loanType"
            value={settings?.loanType || 'ihtiyac'}
            onChange={handleSettingChange}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          >
            <option value="ihtiyac">İhtiyaç Kredisi</option>
            <option value="konut">Konut Kredisi</option>
            <option value="tasit">Taşıt Kredisi</option>
            <option value="kobi">Kobi Kredisi</option>
          </select>
        </div>
      </div>
      
      {/* Example Calculation */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-slate-700">Örnek Hesaplama</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Örnek Tutar
            </label>
            <input
              type="text"
              name="exampleAmount"
              value={content?.exampleAmount || '50.000 TL'}
              onChange={handleContentChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Örnek Vade
            </label>
            <input
              type="text"
              name="exampleTerm"
              value={content?.exampleTerm || '36 ay'}
              onChange={handleContentChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Örnek Faiz Oranı
            </label>
            <input
              type="text"
              name="exampleRate"
              value={content?.exampleRate || '%1,79'}
              onChange={handleContentChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Örnek Aylık Taksit
            </label>
            <input
              type="text"
              name="exampleMonthlyPayment"
              value={content?.exampleMonthlyPayment || '4.771 TL'}
              onChange={handleContentChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Örnek Tahsis Ücreti
            </label>
            <input
              type="text"
              name="exampleAllocationFee"
              value={content?.exampleAllocationFee || '260 TL'}
              onChange={handleContentChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm text-slate-600">
              Örnek Toplam Ödeme
            </label>
            <input
              type="text"
              name="exampleTotalPayment"
              value={content?.exampleTotalPayment || '57.513 TL'}
              onChange={handleContentChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm text-slate-600">
            Örnek Yıllık Maliyet Oranı
          </label>
          <input
            type="text"
            name="exampleAnnualCostRate"
            value={content?.exampleAnnualCostRate || '%30,35'}
            onChange={handleContentChange}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
          />
        </div>
      </div>
      
      {/* Preview */}
      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-2">
          <Calculator size={16} className="text-[#F7BA45]" />
          <h4 className="text-sm font-medium text-slate-700">Önizleme</h4>
        </div>
        <p className="text-sm text-slate-600">
          Maliyet tablosu, {settings?.loanType === 'ihtiyac' ? 'ihtiyaç' : 
            settings?.loanType === 'konut' ? 'konut' : 
            settings?.loanType === 'tasit' ? 'taşıt' : 'kobi'} kredisi için 
          örnek bir hesaplama gösterir. {content?.exampleAmount || '50.000 TL'} tutarında ve {content?.exampleTerm || '36 ay'} vadeli 
          bir kredi için aylık taksit {content?.exampleMonthlyPayment || '4.771 TL'}, toplam ödeme ise {content?.exampleTotalPayment || '57.513 TL'} olacaktır.
        </p>
      </div>
    </div>
  );
};

export default CostTableEditor;